import type { Measurement } from './measure.ts';

/**
 * The measurement, expressed as something that can be drawn in three
 * dimensions - and only the parts of it that legitimately can be.
 *
 * This is the module where the honesty lives. One omnidirectional microphone
 * co-located with one omnidirectional source recovers the *range* to a
 * reflector and nothing whatsoever about its direction. So a detected echo at
 * 2.2 m is not a wall at a place; it is a sphere of radius 2.2 m, every point
 * of which is equally consistent with the data. Drawing it as a sphere states
 * exactly what was learned and exactly what was not.
 *
 * The volume estimate is drawn as a cube for the same reason: Sabine's
 * inversion closes its missing equation by assuming S = 6*V^(2/3), which *is*
 * a cube. Rendering it as anything more specific would be inventing detail the
 * arithmetic never had.
 *
 * Where the shells and the cube disagree, that disagreement is real
 * information - two independent estimates of the same room failing to line up -
 * and it is left visible rather than reconciled.
 */

export const CONFIDENT_EXCESS_DB = 12;

export interface Shell {
  radiusM: number;
  levelDb: number;
  excessDb: number;
  /** Stands far enough above the local reverberation to be called a surface. */
  confident: boolean;
  delayMs: number;
}

export interface RoomModel {
  /** Half-side of the cube implied by the Sabine volume estimate, in metres. */
  cubeHalfM: number;
  volumeM3: number;
  /** Spheres of possible reflector positions, nearest first. */
  shells: Shell[];
  /** Reverberation time, used to drive the decay of the wavefront animation. */
  rt60Sec: number;
  /** Metres per second in this room's air, from the temperature setting. */
  speedOfSoundMs: number;
  /** Nothing closer than this can be seen past the direct blast. */
  blindRangeM: number;
  /** How far out the scene should extend. */
  extentM: number;
  caption: string;
}

export function buildRoomModel(measurement: Measurement): RoomModel {
  const { room, range, decay } = measurement;

  const shells: Shell[] = range.echoes.map((echo) => ({
    radiusM: echo.distanceM,
    levelDb: echo.levelDb,
    excessDb: echo.excessDb,
    confident: echo.excessDb >= CONFIDENT_EXCESS_DB,
    delayMs: echo.delayMs,
  }));

  const volumeM3 = Number.isFinite(room.volumeM3) ? room.volumeM3 : 0;
  const cubeHalfM = Number.isFinite(room.equivalentCubeSideM) ? room.equivalentCubeSideM / 2 : 1.5;

  const furthest = shells.reduce((max, s) => Math.max(max, s.radiusM), 0);
  const extentM = Math.max(cubeHalfM * 1.45, furthest * 1.2, 2.5);

  return {
    cubeHalfM,
    volumeM3,
    shells,
    rt60Sec: Number.isFinite(decay.best.rt60Sec) ? decay.best.rt60Sec : 0.5,
    speedOfSoundMs: range.speedOfSoundMs,
    blindRangeM: range.blindRangeM,
    extentM,
    caption: caption(shells, room.equivalentCubeSideM),
  };
}

function caption(shells: Shell[], cubeSideM: number): string {
  const confident = shells.filter((s) => s.confident);
  const side = Number.isFinite(cubeSideM) ? `${cubeSideM.toFixed(1)} m cube` : 'volume unresolved';

  if (confident.length > 0) {
    const list = confident.map((s) => `${s.radiusM.toFixed(2)} m`).join(', ');
    return `${side} from reverberation · ${confident.length} surface${
      confident.length === 1 ? '' : 's'
    } ranged at ${list}`;
  }
  if (shells.length > 0) {
    return `${side} from reverberation · ${shells.length} weak return${
      shells.length === 1 ? '' : 's'
    }, none clear of the reverberation`;
  }
  return `${side} from reverberation · no return stood clear of the reverberation`;
}
